import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../firebase";

const extOf = (file) => {
  const m = file.name.match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : "bin";
};

const randomId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now());

// Translate Firebase Storage error codes into messages a human can act on.
const explain = (err) => {
  const code = err?.code || "";
  const map = {
    "storage/unauthorized":
      "Storage rules rejected the upload. Make sure you're signed in as an admin and the storage.rules are deployed.",
    "storage/retry-limit-exceeded":
      "Couldn't reach the storage bucket. Check REACT_APP_FIREBASE_STORAGE_BUCKET in .env matches the bucket shown in the Firebase console.",
    "storage/unknown":
      "Storage bucket unreachable or not provisioned. Check the bucket name in .env and that Storage is enabled in the Firebase console.",
    "storage/quota-exceeded": "Storage quota exceeded on this Firebase plan.",
    "storage/canceled": "Upload was canceled.",
  };
  const friendly = map[code];
  const e = new Error(friendly || err?.message || "Upload failed.");
  e.code = code;
  return e;
};

// Uploads a single File and resolves to its public download URL.
// onProgress receives 0..1. Fails fast after `timeoutMs` with a useful
// message instead of letting a bad bucket spin forever.
export const uploadFile = (
  file,
  { folder = "uploads", onProgress, timeoutMs = 45000 } = {}
) =>
  new Promise((resolve, reject) => {
    const path = `${folder}/${randomId()}.${extOf(file)}`;
    const task = uploadBytesResumable(ref(storage, path), file, {
      contentType: file.type || undefined,
    });

    const timer = setTimeout(() => {
      task.cancel();
      reject(
        explain({
          code: "storage/retry-limit-exceeded",
          message: "Upload timed out.",
        })
      );
    }, timeoutMs);

    task.on(
      "state_changed",
      (snap) => onProgress?.(snap.bytesTransferred / snap.totalBytes),
      (err) => {
        clearTimeout(timer);
        reject(explain(err));
      },
      async () => {
        clearTimeout(timer);
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ url, path });
        } catch (err) {
          reject(explain(err));
        }
      }
    );
  });
